import { audioContext } from './audio';

interface Note {
  /** Frequencies played together (a horn is usually two reeds). */
  freqs: number[];
  start: number;
  length: number;
}

type Voice = { type: OscillatorType; volume: number; lowpass: number; notes: Note[] };

/** Each horn is a little score of notes, synthesised live. */
const VOICES: Record<string, Voice> = {
  'trotro-classic': { type: 'sawtooth', volume: 0.16, lowpass: 2200, notes: [{ freqs: [370, 452], start: 0, length: 0.42 }] },
  'pa-pom': {
    type: 'square', volume: 0.12, lowpass: 2600,
    notes: [
      { freqs: [466], start: 0, length: 0.16 }, { freqs: [349], start: 0.2, length: 0.2 },
      { freqs: [466], start: 0.5, length: 0.16 }, { freqs: [349], start: 0.7, length: 0.22 },
    ],
  },
  highlife: {
    type: 'square', volume: 0.1, lowpass: 3200,
    notes: [392, 440, 494, 587, 494, 440, 392].map((f, i) => ({ freqs: [f], start: i * 0.12, length: 0.1 })),
  },
  'air-horn': { type: 'sawtooth', volume: 0.2, lowpass: 900, notes: [{ freqs: [185, 233, 277], start: 0, length: 0.85 }] },
};

export function playHorn(id: string): void {
  const ctx = audioContext();
  const voice = VOICES[id] ?? VOICES['trotro-classic'];
  if (!ctx || !voice) return;
  const now = ctx.currentTime;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = voice.lowpass;
  filter.connect(ctx.destination);
  for (const note of voice.notes) {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now + note.start);
    gain.gain.linearRampToValueAtTime(voice.volume, now + note.start + 0.02);
    gain.gain.setValueAtTime(voice.volume, now + note.start + note.length - 0.04);
    gain.gain.linearRampToValueAtTime(0, now + note.start + note.length);
    gain.connect(filter);
    for (const freq of note.freqs) {
      const osc = ctx.createOscillator();
      osc.type = voice.type;
      // A touch out of tune, like a real trotro horn.
      osc.frequency.value = freq * (1 + (Math.random() - 0.5) * 0.01);
      osc.connect(gain);
      osc.start(now + note.start);
      osc.stop(now + note.start + note.length + 0.05);
    }
  }
}
